import React, { MutableRefObject, RefObject, useMemo } from "react";
import { Platform } from "../../../../types/Platforms";
import { ChatStreamError } from "../../../../services/chatService";
import { Message, UIAction, MessageActions, ImageGenerationData, GeneratedPostsData, GeneratedVideosData, DraftPost, BlogPost, OutreachEmailDraft } from "../types";
import { MessageBubble } from "./MessageBubble";
import { StatusIndicator } from "./StatusIndicator";
import { ToolProgressSteps } from "./ToolProgressSteps";
import { StreamingMessage } from "./StreamingMessage";
import type { ToolStep } from "../hooks/useChatStreaming";
import { ReasoningBlock } from "./ReasoningBlock";
import { ChatErrorBanner } from "./ChatErrorBanner";
import { ChatInputArea } from "./ChatInputArea";
import { PerplexityProgressCard } from "./PerplexityProgressCard";
import { ResearchDocumentCard } from "./ResearchDocumentCard";
import { DeepResearchLoadingCard } from "./DeepResearchLoadingCard";
import { GbpLoadingCard } from "./GbpLoadingCard";
import { GbpBackToCard, backToCardIndex } from "./GbpBackToCard";
import { BulkImageProgressCard } from "./BulkImageProgressCard";
import { ImagePlaceholderTile } from "../../../../features/generate-image/components/ImagePlaceholder";
import { CHAT_COLUMN_CLASS } from "../constants/chatLayout";
import { SaveToPlatformPanel, VideoForPlatform } from "../../../../features/video-management";

type NewChatThreadViewProps = {
  surfaceBg: string;
  isLoadingThreadHistory: boolean;
  messagesContainerRef: RefObject<HTMLDivElement>;
  messagesEndRef: RefObject<HTMLDivElement>;
  lastUserMessageRef: MutableRefObject<HTMLDivElement | null>;
  lastUserMessageIndex: number;
  messages: Message[];
  onPublish: (postData: any) => void;
  onSchedule: (postData: any) => void;
  onSavePosts: (posts: any[]) => void;
  onSaveVideos: (videos: any[]) => void;
  onActionClick: (action: UIAction, context?: any) => void;
  isPublishing: boolean;
  isScheduling: boolean;
  isSavingPosts: boolean;
  isSavingVideos: boolean;
  isLoading: boolean;
  currentStatus: "thinking" | "tool_selected" | "tool_executing" | "generating" | null;
  currentToolName: string | null;
  toolSteps: ToolStep[];
  streamingContent: string;
  streamingThinkingContent?: string;
  streamingActions: MessageActions | null;
  streamingUiActions: UIAction[] | null;
  /** Perplexity citation URLs, updated progressively during streaming (SGL-101). */
  streamingCitations: string[] | null;
  streamingGeneratedImages: ImageGenerationData | null;
  /** True once the current generate_image_batch call's images have arrived — distinct
   *  from streamingGeneratedImages, see useChatStreaming.ts. */
  bulkImagesReady: boolean;
  streamingGeneratedPosts: GeneratedPostsData | null;
  streamingGeneratedVideos?: GeneratedVideosData | null;
  requestInsertImageForPost: (tempId: string) => void | Promise<void>;
  /** Open the right-panel canvas for a specific post. */
  onOpenPostCanvas?: (post: DraftPost, allPosts: GeneratedPostsData) => void;
  /** Open the right-panel blog editor (canvas) for a message's blog post. */
  onOpenBlogCanvas?: (blog: BlogPost) => void;
  /** Applies an edit (subject/body) or Approve to a message's outreach email draft. */
  onOutreachEmailChange?: (message: Message, next: OutreachEmailDraft) => void;
  /** Sends a new chat message asking the model to redraft the outreach email. */
  onOutreachEmailRegenerate?: () => void;
  /** Open the right-panel contact review for a message's parsed batch (SGL-81). */
  onPatchContact?: (message: Message, index: number, fields: import("../../../../services/contactImportService").PatchableContactFields) => Promise<void>;
  onWriteCardIntros?: (message: Message, contacts: import("../types").ScannedCardDetails[], brief: string, captureContext: string) => Promise<void>;
  /** A correction on a scan, recorded before the owner asks the agent anything. */
  onCardDetailsEdited?: (message: Message, contacts: import("../types").ScannedCardDetails[], captureContext: string) => void;
  /** SGL-328: what the Business Profile card can ask the chat window to do. */
  gbpActions?: import("../types").GbpMessageActions;
  onImportContactsFromMessage?: (message: Message, selectedIndices: number[], tags: string[]) => Promise<void>;
  isImportingContacts?: boolean;
  contactUpsertError?: string | null;
  /** Fetch the next page for a message's contacts list (SGL-81 "ask my contacts"). */
  onLoadMoreContacts?: (message: Message) => Promise<void>;
  /** Runs a filtered search, replacing a message's contacts list (SGL-81). */
  onSearchContacts?: (message: Message, query: string) => Promise<void>;
  error: ChatStreamError | string | null;
  onDismissError: () => void;
  chatInputSharedProps: any;
  chipSuggestionsCard?: React.ReactNode;
  showSaveToPlatform: boolean;
  videoForPlatform: VideoForPlatform | null;
  onCloseSavePanel: () => void;
  customerId?: string;
  idToken: string;
  agentId?: string;
  saveToPlatformInitialPlatforms: Platform[];
};

type MessageHistoryListProps = {
  messages: Message[];
  lastUserMessageRef: MutableRefObject<HTMLDivElement | null>;
  lastUserMessageIndex: number;
  onPublish: (postData: any) => void;
  onSchedule: (postData: any) => void;
  onSavePosts: (posts: any[]) => void;
  onSaveVideos: (videos: any[]) => void;
  onActionClick: (action: UIAction, context?: any) => void;
  isPublishing: boolean;
  isScheduling: boolean;
  isSavingPosts: boolean;
  isSavingVideos: boolean;
  isLoading: boolean;
  requestInsertImageForPost: (tempId: string) => void | Promise<void>;
  onOpenPostCanvas?: (post: DraftPost, allPosts: GeneratedPostsData) => void;
  onOpenBlogCanvas?: (blog: BlogPost) => void;
  onOutreachEmailChange?: (message: Message, next: OutreachEmailDraft) => void;
  onOutreachEmailRegenerate?: () => void;
  /** Tenant + caller token for the outreach campaign flow's direct API calls.
   *  Also threaded through to SendDraftCard so an approved reply can be sent
   *  (SGL-244): MessageBubble renders inside this list, not inside
   *  NewChatThreadView, so it can't read customerId from that scope. */
  customerId?: string;
  idToken: string;
  onPatchContact?: (message: Message, index: number, fields: import("../../../../services/contactImportService").PatchableContactFields) => Promise<void>;
  onWriteCardIntros?: (message: Message, contacts: import("../types").ScannedCardDetails[], brief: string, captureContext: string) => Promise<void>;
  /** A correction on a scan, recorded before the owner asks the agent anything. */
  onCardDetailsEdited?: (message: Message, contacts: import("../types").ScannedCardDetails[], captureContext: string) => void;
  /** SGL-328: what the Business Profile card can ask the chat window to do. */
  gbpActions?: import("../types").GbpMessageActions;
  onImportContactsFromMessage?: (message: Message, selectedIndices: number[], tags: string[]) => Promise<void>;
  isImportingContacts?: boolean;
  contactUpsertError?: string | null;
  /** Fetch the next page for a message's contacts list (SGL-81 "ask my contacts"). */
  onLoadMoreContacts?: (message: Message) => Promise<void>;
  /** Runs a filtered search, replacing a message's contacts list (SGL-81). */
  onSearchContacts?: (message: Message, query: string) => Promise<void>;
};

// Split out of NewChatThreadView so a live streaming update (which re-renders
// the parent on every reveal tick) doesn't also reconcile every past message
// bubble. Memoized on `messages` — during a stream, that array reference is
// stable (it's only replaced when a message is added/completed), so this
// subtree bails out entirely while streamingContent keeps changing above it.
const MessageHistoryListComponent: React.FC<MessageHistoryListProps> = ({
  messages,
  lastUserMessageRef,
  lastUserMessageIndex,
  onPublish,
  onSchedule,
  onSavePosts,
  onSaveVideos,
  onActionClick,
  isPublishing,
  isScheduling,
  isSavingPosts,
  isSavingVideos,
  isLoading,
  requestInsertImageForPost,
  onOpenPostCanvas,
  onOpenBlogCanvas,
  onOutreachEmailChange,
  onOutreachEmailRegenerate,
  customerId,
  idToken,
  onPatchContact,
  onWriteCardIntros,
  onCardDetailsEdited,
  gbpActions,
  onImportContactsFromMessage,
  isImportingContacts,
  contactUpsertError,
  onLoadMoreContacts,
  onSearchContacts,
}) => (
  <>
    {messages.map((message, index) => (
      <div
        key={index}
        ref={(el) => {
          if (index === lastUserMessageIndex && el) {
            lastUserMessageRef.current = el;
          }
        }}
        data-message-index={index}
      >
        {message.type === "research_document" && message.researchDocument ? (
          <ResearchDocumentCard doc={message.researchDocument} />
        ) : message.type === "research_pending" ? (
          <DeepResearchLoadingCard startedAt={message.researchStartedAt} />
        ) : message.type === "image_batch_pending" ? (
          <BulkImageProgressCard />
        ) : message.type === "perplexity_progress" ? (
          <PerplexityProgressCard
            query={message.query}
            isComplete={message.isComplete}
          />
        ) : (
          <MessageBubble
            message={message}
            onPublish={onPublish}
            onSchedule={onSchedule}
            onSavePosts={onSavePosts}
            onSaveVideos={onSaveVideos}
            onActionClick={onActionClick}
            isPublishing={isPublishing}
            isScheduling={isScheduling}
            isSavingPosts={isSavingPosts}
            isSavingVideos={isSavingVideos}
            isLoading={isLoading}
            hideEmbeddedPosts={false}
            onInsertImageForPost={requestInsertImageForPost}
            onOpenPostCanvas={onOpenPostCanvas}
            onOpenBlogCanvas={onOpenBlogCanvas}
            onOutreachEmailChange={onOutreachEmailChange}
            onOutreachEmailRegenerate={onOutreachEmailRegenerate}
            customerId={customerId}
            idToken={idToken}
            onPatchContact={onPatchContact}
            onWriteCardIntros={onWriteCardIntros}
            onCardDetailsEdited={onCardDetailsEdited}
            gbpActions={gbpActions}
            onImportContactsFromMessage={onImportContactsFromMessage}
            isImportingContacts={isImportingContacts}
            contactUpsertError={contactUpsertError}
            onLoadMoreContacts={onLoadMoreContacts}
            onSearchContacts={onSearchContacts}
          />
        )}
      </div>
    ))}
  </>
);

const messageHistoryPropsEqual = (
  prev: MessageHistoryListProps,
  next: MessageHistoryListProps,
) =>
  prev.messages === next.messages &&
  prev.lastUserMessageIndex === next.lastUserMessageIndex &&
  prev.isPublishing === next.isPublishing &&
  prev.isScheduling === next.isScheduling &&
  prev.isSavingPosts === next.isSavingPosts &&
  prev.isSavingVideos === next.isSavingVideos &&
  prev.isLoading === next.isLoading &&
  // Missing here, the loading state on a ContactReviewCard's Import button
  // (and any upsert error) never reached the card at all: this boundary
  // bails out on every render where `messages` hasn't also changed, so
  // isImportingContacts flipping true then back to false was silently
  // swallowed in between — confirmed live via a MutationObserver watching
  // the actual button DOM node, which jumped straight from the initial
  // state to the post-upsert state with zero renders in between.
  prev.isImportingContacts === next.isImportingContacts &&
  prev.contactUpsertError === next.contactUpsertError &&
  // Same reasoning as isImportingContacts above, for two more async values.
  // customerId resolves asynchronously, and without it here a draft card
  // rendered before it arrived would stay unable to send with no re-render to
  // fix it. The outreach campaign flow calls the contact and send APIs with
  // both of these, so a refreshed token has to reach it too — and this
  // boundary bails out on every render where `messages` is unchanged, which is
  // most of them.
  prev.customerId === next.customerId &&
  prev.idToken === next.idToken;

const MessageHistoryList = React.memo(
  MessageHistoryListComponent,
  messageHistoryPropsEqual,
);

export const NewChatThreadView: React.FC<NewChatThreadViewProps> = ({
  surfaceBg,
  isLoadingThreadHistory,
  messagesContainerRef,
  messagesEndRef,
  lastUserMessageRef,
  lastUserMessageIndex,
  messages,
  onPublish,
  onSchedule,
  onSavePosts,
  onSaveVideos,
  onActionClick,
  isPublishing,
  isScheduling,
  isSavingPosts,
  isSavingVideos,
  isLoading,
  currentStatus,
  currentToolName,
  toolSteps,
  streamingContent,
  streamingThinkingContent,
  streamingActions,
  streamingUiActions,
  streamingCitations,
  streamingGeneratedImages,
  bulkImagesReady,
  streamingGeneratedPosts,
  requestInsertImageForPost,
  onOpenPostCanvas,
  onOpenBlogCanvas,
  onOutreachEmailChange,
  onOutreachEmailRegenerate,
  onPatchContact,
  onWriteCardIntros,
  onCardDetailsEdited,
  gbpActions,
  onImportContactsFromMessage,
  isImportingContacts,
  contactUpsertError,
  onLoadMoreContacts,
  onSearchContacts,
  error,
  onDismissError,
  chatInputSharedProps,
  chipSuggestionsCard,
  showSaveToPlatform,
  videoForPlatform,
  onCloseSavePanel,
  customerId,
  idToken,
  agentId,
  saveToPlatformInitialPlatforms,
}) => {
  // Deep research streams nothing for minutes (only keepalives). Show the animated
  // loading card from the moment the tool starts, through the holding message, until
  // the report (or an error) arrives. currentToolName is cleared once the holding
  // `content` event lands, so we also detect the holding message text itself.
  const isDeepResearchLoading =
    isLoading &&
    (currentToolName === "deep_research" ||
      (streamingContent || "").includes("Generating your research report"));

  // Image generation is silent for several seconds; show the animated, meaningful
  // loading card (instead of a bare status) until the first image streams in.
  const isImageGenerating =
    isLoading &&
    currentToolName === "generate_image" &&
    !streamingGeneratedImages;

  // Bulk image batches (generate_image_batch) take 30-180s across N generations —
  // show the reassurance card + email-notify toggle instead of a bare status line.
  // (docs/bulk-image-email-notification-plan.md)
  // bulkImagesReady (not streamingGeneratedImages) so an earlier generate_image call
  // in the same turn can't make this look "already done" before the batch finishes.
  // The agent is reading the Business Profile: text already streamed is final
  // for now, so show it crisp with a status under it rather than mid-fade.
  const gbpStep = isLoading
    ? toolSteps.find((s) => s.tool === "review_google_business_profile")
    : undefined;
  const isGbpReading = gbpStep?.state === "running";
  // The agent chose to open the fix card; it opens when the turn ends. Until
  // then show the card's own loading state, so the wait reads as one step.
  const isGbpCardOpening = !!gbpStep?.opensCard;
  const backToCard = useMemo(() => backToCardIndex(messages, isLoading), [messages, isLoading]);

  const isBulkImageGenerating =
    isLoading &&
    currentToolName === "generate_image_batch" &&
    !bulkImagesReady;

  return (
    <>
      <div
        ref={messagesContainerRef}
        className={`min-h-0 flex-1 space-y-8 overflow-y-auto px-4 pb-[200px] pt-6 sm:px-6 lg:pb-[280px] ${surfaceBg}`}
      >
        {isLoadingThreadHistory ? (
          <div className="flex min-h-[240px] flex-col items-center justify-center gap-3 py-12">
            <div className="h-10 w-10 animate-spin rounded-full border-2 border-theme-fg-muted border-t-transparent" />
            <p className="text-sm text-theme-fg-muted">Loading conversation…</p>
          </div>
        ) : messages.length > 0 ? (
          <>
            <MessageHistoryList
              messages={messages}
              lastUserMessageRef={lastUserMessageRef}
              lastUserMessageIndex={lastUserMessageIndex}
              onPublish={onPublish}
              onSchedule={onSchedule}
              onSavePosts={onSavePosts}
              onSaveVideos={onSaveVideos}
              onActionClick={onActionClick}
              isPublishing={isPublishing}
              isScheduling={isScheduling}
              isSavingPosts={isSavingPosts}
              isSavingVideos={isSavingVideos}
              isLoading={isLoading}
              requestInsertImageForPost={requestInsertImageForPost}
              onOpenPostCanvas={onOpenPostCanvas}
              onOpenBlogCanvas={onOpenBlogCanvas}
              onOutreachEmailChange={onOutreachEmailChange}
              onOutreachEmailRegenerate={onOutreachEmailRegenerate}
              customerId={customerId}
              idToken={idToken}
              onPatchContact={onPatchContact}
              onWriteCardIntros={onWriteCardIntros}
              onCardDetailsEdited={onCardDetailsEdited}
              gbpActions={gbpActions}
              onImportContactsFromMessage={onImportContactsFromMessage}
              isImportingContacts={isImportingContacts}
              contactUpsertError={contactUpsertError}
              onLoadMoreContacts={onLoadMoreContacts}
              onSearchContacts={onSearchContacts}
            />
            {isDeepResearchLoading ? (
              <DeepResearchLoadingCard />
            ) : (
            <>
            {isLoading && !streamingContent && !streamingThinkingContent && (
              // While the fix card is opening, its own loading state shows instead.
              isGbpCardOpening ? null : isBulkImageGenerating ? (
                <BulkImageProgressCard />
              ) : isImageGenerating ? (
                <div className={CHAT_COLUMN_CLASS}>
                  <div className="max-w-[280px]">
                    <ImagePlaceholderTile />
                  </div>
                </div>
              ) : toolSteps.length > 0 ? (
                <ToolProgressSteps steps={toolSteps} />
              ) : (
                <StatusIndicator
                  status={currentStatus}
                  toolName={currentToolName || undefined}
                />
              )
            )}
            {streamingThinkingContent && (
              <ReasoningBlock
                content={streamingThinkingContent}
                isStreaming={isLoading && !streamingContent}
              />
            )}
            {(streamingContent ||
              streamingGeneratedPosts ||
              streamingGeneratedImages) && (
              <StreamingMessage
                content={streamingContent}
                citations={streamingCitations}
                actions={streamingActions}
                uiActions={streamingUiActions}
                generatedImages={streamingGeneratedImages}
                generatedPosts={streamingGeneratedPosts}
                onPublish={onPublish}
                onSchedule={onSchedule}
                onSavePosts={onSavePosts}
                onSaveVideos={onSaveVideos}
                onActionClick={onActionClick}
                isPublishing={isPublishing}
                isScheduling={isScheduling}
                isSavingPosts={isSavingPosts}
                isSavingVideos={isSavingVideos}
                isLoading={isLoading}
                isAnimating={isLoading && !isGbpReading}
                hideEmbeddedPosts={false}
                onInsertImageForPost={requestInsertImageForPost}
                onOpenPostCanvas={onOpenPostCanvas}
              />
            )}
            {isGbpCardOpening && (
              <div className={`${CHAT_COLUMN_CLASS} animate-fadeIn`}>
                <GbpLoadingCard quiet />
              </div>
            )}
            </>
            )}
            <div ref={messagesEndRef} />
          </>
        ) : (
          <div className="flex min-h-[200px] flex-col items-center justify-center py-12 text-center">
            <p className="text-sm text-theme-fg-muted">No messages yet. Start the conversation below.</p>
          </div>
        )}

        {error && (
          <div className={CHAT_COLUMN_CLASS}>
            <ChatErrorBanner error={error} onDismiss={onDismissError} compact />
          </div>
        )}
      </div>

      {showSaveToPlatform && videoForPlatform && (
        <SaveToPlatformPanel
          isOpen={showSaveToPlatform}
          onClose={onCloseSavePanel}
          video={videoForPlatform}
          customerId={customerId || ""}
          idToken={idToken}
          agentId={agentId || ""}
          initialSelectedPlatforms={saveToPlatformInitialPlatforms}
        />
      )}

      {chipSuggestionsCard}
      {/* While the owner talks about the Business Profile card: a way back to it. */}
      {backToCard >= 0 && (
        <div className={`flex-shrink-0 px-4 pt-2 sm:px-6 ${surfaceBg}`}>
          <div className={CHAT_COLUMN_CLASS}>
            <GbpBackToCard index={backToCard} />
          </div>
        </div>
      )}
      <ChatInputArea {...chatInputSharedProps} variant="docked" />
    </>
  );
};
